import { useCallback, useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext, useBeforeUnload } from 'react-router-dom';

const DISCARD_MESSAGE = 'Alle nicht gespeicherten Eingaben gehen verloren. Möchtest du die Seite wirklich verlassen?';

let activeHistoryGuard: ((event: PopStateEvent) => void) | undefined;
// Window's popstate listeners run in registration order. Register before BrowserRouter
// mounts; a listener installed by the form effect runs after the router has unmounted it.
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (event) => activeHistoryGuard?.(event), true);
}

/**
 * RITA-1547: Statt der Browserfrage eine eigene Rückfrage öffnen. Erhält die Fortsetzung der verhinderten
 * Navigation; ruft die Rückfrage sie auf, wird ohne erneute Frage navigiert. Nur für Navigationen
 * innerhalb des PRM (push/replace); Zurück-Pfeil, Neuladen und Schließen behalten die Browserfrage.
 */
export type BlockierteNavigation = (fortsetzen: () => void) => void;

/** BrowserRouter has no data-router blocker; guard its navigator and native history changes. */
export default function useTemplateNavigationGuard(dirty: boolean, onBlockiert?: BlockierteNavigation): () => void {
  const { navigator } = useContext(NavigationContext);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const blockiertRef = useRef(onBlockiert);
  blockiertRef.current = onBlockiert;

  const allowNavigation = useCallback(() => {
    dirtyRef.current = false;
  }, []);

  useBeforeUnload(
    useCallback((event) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    }, []),
  );

  useEffect(() => {
    const canLeave = () => {
      if (!dirtyRef.current) return true;
      // eslint-disable-next-line no-alert
      return window.confirm(DISCARD_MESSAGE);
    };
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const originalGo = navigator.go;
    // RITA-1547: Mit eigener Rückfrage wird nicht sofort entschieden; sie setzt die Navigation ggf. fort.
    const eigeneRueckfrage = (fortsetzen: () => void): boolean => {
      const rueckfrage = blockiertRef.current;
      if (!dirtyRef.current || !rueckfrage) return false;
      rueckfrage(() => {
        allowNavigation();
        fortsetzen();
      });
      return true;
    };
    const guardedPush: typeof originalPush = (...args) => {
      if (eigeneRueckfrage(() => originalPush.apply(navigator, args))) return;
      if (canLeave()) originalPush.apply(navigator, args);
    };
    const guardedReplace: typeof originalReplace = (...args) => {
      if (eigeneRueckfrage(() => originalReplace.apply(navigator, args))) return;
      if (canLeave()) originalReplace.apply(navigator, args);
    };
    const guardedGo: typeof originalGo = (...args) => {
      if (canLeave()) {
        allowNavigation();
        originalGo.apply(navigator, args);
      }
    };
    navigator.push = guardedPush;
    navigator.replace = guardedReplace;
    navigator.go = guardedGo;

    const currentState = window.history.state;
    const currentUrl = window.location.href;
    const currentIndex: unknown = currentState?.idx;
    let restoring = false;
    const handlePopState = (event: PopStateEvent) => {
      if (restoring) {
        restoring = false;
        event.stopImmediatePropagation();
        return;
      }
      if (canLeave()) return;
      // Never let the router unmount a draft after the user cancelled leaving.
      event.stopImmediatePropagation();
      const targetIndex: unknown = event.state?.idx;
      if (typeof currentIndex === 'number' && typeof targetIndex === 'number' && currentIndex !== targetIndex) {
        restoring = true;
        window.history.go(currentIndex - targetIndex);
      } else {
        // Entries created outside React Router have no reliable traversal direction.
        // Restore the editor entry instead of guessing forward/back and losing the draft.
        window.history.pushState(currentState, '', currentUrl);
      }
    };
    activeHistoryGuard = handlePopState;
    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      navigator.go = originalGo;
      if (activeHistoryGuard === handlePopState) activeHistoryGuard = undefined;
    };
  }, [allowNavigation, navigator]);

  return allowNavigation;
}
